import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuthStore } from '@/stores/authStore';

function mapOAuthError(errorCode: string | null): string | null {
  switch (errorCode) {
    case 'no_code':
      return 'Google login was cancelled or returned no authorization code. Please try again.';
    case 'not_configured':
      return 'Google sign-in is not configured on the server.';
    case 'token_error':
      return 'Google sign-in failed while exchanging credentials. Please try again.';
    case 'user_info_error':
      return 'Google sign-in failed while reading your profile.';
    case 'google_email_not_verified':
      return 'Your Google email is not verified. Verify it in Google first, then try again.';
    case 'google_account_already_linked':
      return 'This Google account is already linked to another user.';
    case 'link_failed':
      return 'We could not link your Google account. Please try again.';
    case 'oauth_error':
      return 'Google sign-in failed unexpectedly. Please try again.';
    default:
      return null;
  }
}

export function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [searchParams] = useSearchParams();
  const login = useAuthStore((state) => state.login);
  const loginWithGoogle = useAuthStore((state) => state.loginWithGoogle);
  const error = useAuthStore((state) => state.error);
  const isLoading = useAuthStore((state) => state.isLoading);
  const clearError = useAuthStore((state) => state.clearError);
  const navigate = useNavigate();
  const oauthError = mapOAuthError(searchParams.get('error'));
  const errorMessage = error ?? oauthError;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await login(email, password);
      navigate('/');
    } catch {
      // Error is handled by store
    }
  };

  return (
    <div className="flex items-center justify-center h-full p-4">
      <div className="bg-wall-surface border border-wall-border rounded-lg p-8 w-full max-w-md">
        <h1 className="text-2xl font-bold text-center mb-6">Login to The Wall</h1>

        {errorMessage && (
          <div className="bg-red-900/50 border border-red-500 text-red-200 px-4 py-2 rounded-sm mb-4">
            {errorMessage}
            <button
              onClick={() => {
                clearError();
                navigate('/login', { replace: true });
              }}
              className="float-right text-red-400 hover:text-red-200"
            >
              ×
            </button>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-wall-muted mb-1">
              Email
            </label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-4 py-2 bg-wall-bg border border-wall-border rounded-sm focus:outline-hidden focus:ring-2 focus:ring-wall-accent"
              required
            />
          </div>

          <div>
            <div className="flex items-center justify-between mb-1">
              <label htmlFor="password" className="block text-sm font-medium text-wall-muted">
                Password
              </label>
              <Link
                to="/forgot-password"
                onClick={clearError}
                className="text-sm text-wall-accent hover:underline"
              >
                Forgot password?
              </Link>
            </div>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-4 py-2 bg-wall-bg border border-wall-border rounded-sm focus:outline-hidden focus:ring-2 focus:ring-wall-accent"
              required
            />
          </div>

          <button
            type="submit"
            disabled={isLoading}
            className="w-full py-2 bg-wall-accent hover:bg-opacity-80 rounded-sm font-medium transition-colors disabled:opacity-50"
          >
            {isLoading ? 'Logging in...' : 'Login'}
          </button>
        </form>

        <div className="my-6 flex items-center">
          <div className="flex-1 border-t border-wall-border" />
          <span className="px-4 text-wall-muted text-sm">or</span>
          <div className="flex-1 border-t border-wall-border" />
        </div>

        <button
          onClick={loginWithGoogle}
          className="w-full py-2 bg-white text-gray-800 hover:bg-gray-100 rounded-sm font-medium transition-colors flex items-center justify-center gap-2"
        >
          <svg className="w-5 h-5" viewBox="0 0 24 24">
            <path
              fill="currentColor"
              d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
            />
            <path
              fill="currentColor"
              d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
            />
            <path
              fill="currentColor"
              d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
            />
            <path
              fill="currentColor"
              d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
            />
          </svg>
          Continue with Google
        </button>

        <p className="text-center text-wall-muted mt-6">
          Don't have an account?{' '}
          <Link to="/register" className="text-wall-accent hover:underline">
            Register
          </Link>
        </p>
      </div>
    </div>
  );
}
